/**
 * Web implementation of the shared-core FeedbackCapture port: the page screenshot and the console error buffer
 * the quick-note panel reports (feedback quick capture spec 01 §2).
 *
 * Browser globals live here by design (`apps/frontend/src/platform/`). Nothing is sent from here: the panel reads
 * both and decides what goes in a note.
 */
import { domToBlob } from "modern-screenshot";
import {
  FEEDBACK_IGNORE_ATTRIBUTE,
  type FeedbackCapture,
} from "@shared-core/capabilities/feedback-capture";
import { safeConsoleMessage } from "@shared-core/domain/feedback-quick-note";

/** The widest a screenshot is drawn, in pixels: legible text, and well inside the 10 MB image cap. */
const MAX_SCREENSHOT_WIDTH = 1600;
const SCREENSHOT_TIMEOUT_MS = 10_000;
const CONSOLE_ERRORS_KEPT = 5;

const consoleErrors: { message: string }[] = [];
let consoleHookInstalled = false;

function remember(text: string): void {
  const message = safeConsoleMessage(text);
  if (!message) return;
  consoleErrors.push({ message });
  if (consoleErrors.length > CONSOLE_ERRORS_KEPT) consoleErrors.shift();
}

function textOf(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === "string") return arg;
  if (
    typeof arg === "number" ||
    typeof arg === "boolean" ||
    typeof arg === "bigint"
  ) {
    return String(arg);
  }
  // An object's contents may be user data; its kind is enough to tell errors apart.
  return `[${Object.prototype.toString.call(arg).slice(8, -1)}]`;
}

/**
 * Start remembering console errors, uncaught errors and unhandled promise rejections. Safe to call more than once.
 * The original `console.error` still runs first, so nothing is hidden from the developer tools.
 */
export function installConsoleErrorHook(): void {
  if (consoleHookInstalled) return;
  consoleHookInstalled = true;
  const original = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    original(...args);
    remember(args.map(textOf).join(" "));
  };
  window.addEventListener("error", (event) => {
    remember(event.error ? textOf(event.error) : event.message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    remember(`Unhandled rejection: ${textOf(event.reason)}`);
  });
}

async function screenshotOfPage(): Promise<Blob> {
  const { innerWidth, innerHeight, scrollX, scrollY } = window;
  const scale = Math.min(1, MAX_SCREENSHOT_WIDTH / innerWidth);
  const background =
    getComputedStyle(document.body).backgroundColor || "#ffffff";
  const drawn = domToBlob(document.documentElement, {
    type: "image/png",
    width: innerWidth,
    height: innerHeight,
    scale,
    backgroundColor: background,
    timeout: SCREENSHOT_TIMEOUT_MS,
    // The visible part of a scrolled page, not its top.
    style:
      scrollX || scrollY
        ? {
            transform: `translate(${-scrollX}px, ${-scrollY}px)`,
            transformOrigin: "0 0",
          }
        : {},
    filter: (node) =>
      !(
        node instanceof Element && node.hasAttribute(FEEDBACK_IGNORE_ATTRIBUTE)
      ),
  });
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(
      () => reject(new Error("Drawing the page took too long.")),
      SCREENSHOT_TIMEOUT_MS + 2_000,
    ),
  );
  return Promise.race([drawn, timeout]);
}

export const webFeedbackCapture: FeedbackCapture = {
  captureScreenshot: screenshotOfPage,
  recentConsoleErrors: () => [...consoleErrors],
};
